import React, { useState } from 'react';
import { Download, Printer } from 'lucide-react';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  SectionHeader,
  SegmentedControl,
  Switch,
} from '../ui';

export interface ReportSection {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  required?: boolean;
}

export interface ReportConfig {
  sections: ReportSection[];
  includeImages: boolean;
  includeCharts: boolean;
  format: 'pdf' | 'print';
}

interface ReportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: ReportConfig) => void;
  entityType: 'stakeholder' | 'organization';
  entityName: string;
}

const getDefaultSections = (entityType: 'stakeholder' | 'organization'): ReportSection[] => {
  if (entityType === 'stakeholder') {
    return [
      {
        id: 'personal-info',
        title: 'Personal Information',
        description: 'Name, contact details, location, bio',
        enabled: true,
        required: true
      },
      {
        id: 'assignments',
        title: 'Current Assignments',
        description: 'Organizations, roles, and responsibilities',
        enabled: true,
        required: true
      },
      {
        id: 'contact-details',
        title: 'Contact Information',
        description: 'Professional and personal contact details',
        enabled: true
      },
      {
        id: 'social-media',
        title: 'Social Media & Links',
        description: 'LinkedIn, Twitter, Facebook, and other profiles',
        enabled: true
      },
      {
        id: 'influence-data',
        title: 'Influence Analysis',
        description: 'Influence scores and network analysis',
        enabled: true
      },
      {
        id: 'media-assets',
        title: 'Media & Documents',
        description: 'Profile images and associated files',
        enabled: false
      },
      {
        id: 'wiki-content',
        title: 'Wiki Documentation',
        description: 'Associated wiki pages and notes',
        enabled: false
      }
    ];
  } else {
    return [
      {
        id: 'organization-info',
        title: 'Organization Details',
        description: 'Name, type, description, contact information',
        enabled: true,
        required: true
      },
      {
        id: 'stakeholders',
        title: 'Key Stakeholders',
        description: 'Current team members and their roles',
        enabled: true,
        required: true
      },
      {
        id: 'hierarchy',
        title: 'Organizational Structure',
        description: 'Parent/child relationships and hierarchy',
        enabled: true
      },
      {
        id: 'contact-details',
        title: 'Contact Information',
        description: 'Address, phone, email, website',
        enabled: true
      },
      {
        id: 'influence-data',
        title: 'Influence Analysis',
        description: 'Organization influence scores and metrics',
        enabled: true
      },
      {
        id: 'media-assets',
        title: 'Media & Documents',
        description: 'Logo, images, and associated files',
        enabled: false
      },
      {
        id: 'wiki-content',
        title: 'Wiki Documentation',
        description: 'Associated wiki pages and notes',
        enabled: false
      }
    ];
  }
};

export const ReportConfigModal: React.FC<ReportConfigModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  entityType,
  entityName
}) => {
  const [sections, setSections] = useState<ReportSection[]>(() =>
    getDefaultSections(entityType)
  );
  const [includeImages, setIncludeImages] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [format, setFormat] = useState<'pdf' | 'print'>('pdf');

  const handleSectionToggle = (sectionId: string) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, enabled: !section.enabled }
        : section
    ));
  };

  const handleSelectAll = () => {
    setSections(prev => prev.map(section => ({ ...section, enabled: true })));
  };

  const handleSelectNone = () => {
    setSections(prev => prev.map(section =>
      section.required ? section : { ...section, enabled: false }
    ));
  };

  const handleGenerate = () => {
    const config: ReportConfig = {
      sections,
      includeImages,
      includeCharts,
      format
    };
    onGenerate(config);
  };

  const enabledSections = sections.filter(s => s.enabled).length;
  const totalSections = sections.length;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      labelledBy="report-config-title"
      handle
      className="max-h-[92vh] sm:max-h-[86vh] sm:max-w-lg"
    >
      <DialogHeader
        id="report-config-title"
        eyebrow={<span className="block max-w-[260px] truncate sm:max-w-[360px]">{entityName}</span>}
        title="Report"
        onClose={onClose}
      />

      <DialogBody className="space-y-5">
        <SegmentedControl
          label="Output format"
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
          value={format}
          onChange={setFormat}
          items={[
            {
              value: 'pdf',
              label: (
                <>
                  <Download className="h-4 w-4" strokeWidth={1.75} />
                  PDF
                </>
              ),
            },
            {
              value: 'print',
              label: (
                <>
                  <Printer className="h-4 w-4" strokeWidth={1.75} />
                  Print
                </>
              ),
            },
          ]}
        />

        <section>
          <SectionHeader
            title={
              <>
                Sections{' '}
                <span className="text-[12px] tabular-nums text-neutral-400">
                  {enabledSections}/{totalSections}
                </span>
              </>
            }
            aside={
              <>
                <Button size="xs" variant="ghost" onClick={handleSelectAll}>
                  All
                </Button>
                <Button size="xs" variant="ghost" onClick={handleSelectNone}>
                  None
                </Button>
              </>
            }
          />
          <Card divided>
            {sections.map(section => (
              <div key={section.id} title={section.description} className="flex items-center transition-colors hover:bg-neutral-50">
                <Checkbox
                  id={`report-section-${section.id}`}
                  checked={section.enabled}
                  disabled={section.required}
                  onChange={() => !section.required && handleSectionToggle(section.id)}
                  className={`min-h-[44px] w-full gap-3 px-4 ${section.required ? 'cursor-default' : ''}`}
                  label={
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                      <span className="truncate">{section.title}</span>
                      {section.required && <span className="flex-shrink-0 text-[12px] text-neutral-400">Required</span>}
                    </span>
                  }
                />
              </div>
            ))}
          </Card>
        </section>

        <Card divided>
          <div className="flex min-h-[44px] items-center justify-between gap-3 px-4">
            <span className="text-[13px]">Images and media</span>
            <Switch label="Include images and media" checked={includeImages} onChange={setIncludeImages} />
          </div>
          <div className="flex min-h-[44px] items-center justify-between gap-3 px-4">
            <span className="text-[13px]">Charts</span>
            <Switch label="Include charts" checked={includeCharts} onChange={setIncludeCharts} />
          </div>
        </Card>
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleGenerate} disabled={enabledSections === 0}>
          Preview
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
